import React, { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/Tabs';
import { ApiKeysSection } from '../components/ApiKeysSection';
import { ProductSyncSection } from '../components/ProductSyncSection';
import { VendedoresSection } from '../components/VendedoresSection';
import { PriceListImportSection } from '../components/PriceListImportSection';
import { PriceListTable, PriceListTableHandle } from '../components/PriceListTable';
import { PriceListDetail } from '../components/PriceListDetail';
import { ProductsSection } from '../components/ProductsSection';
import { SaveToast } from '../components/SaveToast';
import { useSaveToast } from '../hooks/useSaveToast';

const SLUG_TO_TAB: Record<string, string> = {
  'credenciales': 'api-keys',
  'sincronizacion': 'sync',
  'vendedores': 'vendedores',
  'listas-de-precios': 'price-lists',
  'productos': 'products',
};

const TAB_TO_SLUG: Record<string, string> = {
  'api-keys': 'credenciales',
  'sync': 'sincronizacion',
  'vendedores': 'vendedores',
  'price-lists': 'listas-de-precios',
  'products': 'productos',
};

interface SettingsProps {
  onMenuClick?: () => void;
}

export const Settings: React.FC<SettingsProps> = ({ onMenuClick }) => {
  const { tab, listId } = useParams<{ tab?: string; listId?: string }>();
  const navigate = useNavigate();
  const priceListTableRef = useRef<PriceListTableHandle>(null);
  const { visible: toastVisible, notify } = useSaveToast();

  const activeTab = (tab && SLUG_TO_TAB[tab]) || (listId ? 'price-lists' : 'api-keys');
  const selectedListId = listId ? parseInt(listId, 10) : null;

  const handleTabChange = (v: string) => {
    navigate(`/configuracion/${TAB_TO_SLUG[v] ?? v}`);
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <Header onMenuClick={onMenuClick} />

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6">
          <h1 className="font-sans font-bold text-2xl text-foreground mb-6">
            Configuración
          </h1>

          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="border-b border-border mb-6">
              <TabsTrigger value="price-lists">Listas de precios</TabsTrigger>
              <TabsTrigger value="products">Productos</TabsTrigger>
              <TabsTrigger value="api-keys">Credenciales</TabsTrigger>
              <TabsTrigger value="sync">Sincronización</TabsTrigger>
              <TabsTrigger value="vendedores">Vendedores</TabsTrigger>
            </TabsList>

            <TabsContent value="api-keys">
              <ApiKeysSection onSaved={notify} />
            </TabsContent>

            <TabsContent value="sync">
              <ProductSyncSection />
            </TabsContent>

            <TabsContent value="vendedores">
              <VendedoresSection onSaved={notify} />
            </TabsContent>

            <TabsContent value="price-lists">
              {selectedListId === null ? (
                <>
                  <PriceListTable
                    ref={priceListTableRef}
                    onSelectList={(id) => navigate(`/configuracion/listas-de-precios/${id}`)}
                    onSaved={notify}
                  />
                  <hr className="my-8 border-border" />
                  <PriceListImportSection onImportSuccess={() => priceListTableRef.current?.reload()} />
                </>
              ) : (
                <PriceListDetail
                  listId={selectedListId}
                  onBack={() => {
                    navigate('/configuracion/listas-de-precios');
                    priceListTableRef.current?.reload();
                  }}
                  onSaved={notify}
                />
              )}
            </TabsContent>

            <TabsContent value="products">
              <ProductsSection onSaved={notify} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <SaveToast visible={toastVisible} />
    </div>
  );
};

export default Settings;
